import { FC, ReactElement, useContext, useEffect, useState } from 'react';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import ProgressIndicator from '@components/ProgressIndicator';
import Spacer from '@components/Spacer';
import Message from '@components/DesignLibrary/Message';
import InputComposer from '@components/DesignLibrary/InputComposer';
import { useIsMobile } from '@hooks';
import * as configuration from '@/configuration/client';
import TeamsContext from '@providers/Teams/TeamsContext';
import Image from 'next/legacy/image';
import { SubmissionAIMessage } from '@typings/integrations-api-models';
import useSocketContext from '@providers/Socket/useSocketContext';
import { SocketMessageType } from '@providers/Socket';
import Head from 'next/head';
import Buttons from '@/components/Buttons';

import s from './PacerSubmissionPane.module.scss';

type Props = {
  submissionAttemptId: string;
};

const PacerSubmissionPane: FC<Props> = ({ submissionAttemptId }) => {
  const { getData: getMessages } = useNoodleApi(tsClient.integrations.getCaseSubmissionMessages);
  const { getData: getSingleMessage } = useNoodleApi(tsClient.integrations.getCaseSubmissionMessage);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const [messages, setMessages] = useState<SubmissionAIMessage[]>([]);

  const isMobile = useIsMobile();
  const { currentTeamOwner } = useContext(TeamsContext);

  const { addListener, removeListener, isInitialized: isSocketInitialized, joinChannels } = useSocketContext();

  useEffect(() => {
    const getData = async (): Promise<void> => {
      const { data: initialMessages } = await getMessages({
        id: submissionAttemptId,
        page: 1,
        perPage: 100_000,
      });
      if (initialMessages) {
        setMessages(initialMessages.items);
      }
    };
    getData();
  }, [submissionAttemptId, getMessages]);

  // Scroll to bottom every time there is a new message
  useEffect(() => {
    const rightPanel = document.getElementById('discussionPanel');
    if (rightPanel) {
      rightPanel.scroll(0, rightPanel.scrollHeight);
    }
  }, [messages]);

  useEffect(() => {
    if (isSocketInitialized && submissionAttemptId) {
      joinChannels([`submission-attempts/${submissionAttemptId}`]);
    }
  }, [isSocketInitialized, joinChannels, submissionAttemptId]);

  useEffect(() => {
    const handleNewMessage = async (data: unknown): Promise<void> => {
      // eslint-disable-next-line no-console
      console.log('received new AI message', data);
      const { messageId, submissionAttemptId: messageSubmissionAttemptId } = data as { messageId: string; submissionAttemptId: string };
      if (messageSubmissionAttemptId !== submissionAttemptId) {
        return;
      }
      const { data: newMessage } = await getSingleMessage({ id: submissionAttemptId, messageId });
      if (newMessage) {
        setMessages(prev => [newMessage, ...prev]);
      }
    };
    const listenerId = addListener({
      fn: handleNewMessage,
      messageType: SocketMessageType.AI_CASE_SUBMISSION_MESSAGE,
    });
    return () => {
      removeListener(listenerId);
    };
  }, [addListener, removeListener, getSingleMessage, submissionAttemptId]);

  if (!messages || !currentTeamOwner) {
    return (
      <>
        <Spacer />
        <ProgressIndicator isCentered isPage />
      </>
    );
  }

  const renderMessage = (
    m: Awaited<ReturnType<typeof tsClient.integrations.getCaseSubmissionMessages>>['items'][number],
  ): ReactElement => (
    <div className={s.message} id={m.id}>
      <Message
        creator={{
          ...currentTeamOwner,
          personId: currentTeamOwner.person.id,
        }}
        isAi={false}
        timestamp={m.createdAt}
        message={{
          contextId: null,
          contextTitle: null,
          contextType: null,
          createdAt: m.createdAt,
          id: m.id,
          isDeleted: false,
          text: m.text,
          title: null,
        }}
        compact
        user={{
          id: '',
          image: {
            url: '/glade-logo.png',
          },
          name: 'Glade.ai',
        }}
        isReplyView={false}
      />
      {m.s3Key && (
        <Buttons className={s.image} isWrapper onClick={() => {
          setImageUrl(`${configuration.COMPUTER_USE_CLOUDFRONT_HOST}/${m.s3Key}`);
        }}>
          <Image width={250} height={200} alt='screenshot' src={`${configuration.COMPUTER_USE_CLOUDFRONT_HOST}/${m.s3Key}`} />
        </Buttons>
      )}
    </div>
  );

  return (
    <>
      <Head>
        <title>PACER · Glade Dashboard</title>
      </Head>
      <div className={s.pane}>
        <div className={s.content}>
          {imageUrl && (
            <Image layout='fill' alt='screenshot' src={imageUrl} className={s.screenshot} />
          )}
        </div>
        <div id='discussionPanel' className={s.discussion}>
          {messages.length > 0 && (
            <div className={s.messages}>
              <>
                {[...messages].reverse().map(m => (
                  <div key={m.id}>
                    <div>
                      {renderMessage(m)}
                    </div>
                  </div>
                ))}
              </>
            </div>
          )}
          {messages.length === 0 && (
            <div className={s.emptyState}>
              <p className="body-sm">No messages yet</p>
            </div>
          )}
          <Spacer />
          <div id="inputComposer" className={s.inputComposer} style={isMobile ? { paddingBottom: 76 } : undefined}>
            <InputComposer
              showSendButton={false}
              attachmentTypes={['File', 'Photo', 'Video', 'Audio']}
              isSending={false}
              onSubmit={() => {}}
              referenceId={submissionAttemptId}
              referenceType="ai-message"
              isAsync={false}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default PacerSubmissionPane;
